// src/components/admin/CostSettingsModal.js

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { IoClose, IoSaveOutline, IoCashOutline } from 'react-icons/io5';

const COST_SETTINGS_KEY = 'adminCostSettings';

const CostInput = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="text-s-text mb-1 block text-sm font-medium">
      {label}
    </label>
    <div className="relative">
      <span className="text-s-text absolute inset-y-0 left-0 flex items-center pl-3">
        $
      </span>
      <input
        type="number"
        step="0.01"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        placeholder={placeholder}
        className="bg-2 border-line text-p-text w-full rounded-lg border py-2 pl-7 pr-3 focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
      />
    </div>
  </div>
);

export const CostSettingsModal = ({ isOpen, onClose, onSave }) => {
  const [settings, setSettings] = useState({
    tasks: { input: 0, output: 0 },
    workflows: { input: 0, output: 0 },
  });

  useEffect(() => {
    if (isOpen) {
      const savedSettings = JSON.parse(localStorage.getItem(COST_SETTINGS_KEY));
      if (savedSettings) {
        setSettings(savedSettings);
      }
    }
  }, [isOpen]);

  const handleSave = () => {
    localStorage.setItem(COST_SETTINGS_KEY, JSON.stringify(settings));
    onSave(settings);
    toast.success('Cost settings saved!');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div className="bg-1 animate-fade-in-up flex w-full max-w-lg flex-col rounded-2xl shadow-2xl">
        <div className="border-line flex items-center justify-between border-b p-4">
          <h2 className="text-p-text flex items-center gap-2 text-xl font-bold">
            <IoCashOutline /> Configure Token Costs
          </h2>
          <button onClick={onClose} className="hover:bg-2 rounded-full p-2">
            <IoClose className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-6 p-6">
          <p className="text-s-text text-sm">
            Enter the cost in US Dollars ($) per 1 million tokens for each
            category. These values are saved locally in your browser.
          </p>
          <div className="bg-body border-line space-y-4 rounded-lg border p-4">
            <h3 className="text-p-text font-semibold">Task Usage Costs</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <CostInput
                label="Input Cost ($ / 1M tokens)"
                value={settings.tasks.input}
                onChange={(v) =>
                  setSettings((s) => ({
                    ...s,
                    tasks: { ...s.tasks, input: v },
                  }))
                }
                placeholder="e.g., 0.50"
              />
              <CostInput
                label="Output Cost ($ / 1M tokens)"
                value={settings.tasks.output}
                onChange={(v) =>
                  setSettings((s) => ({
                    ...s,
                    tasks: { ...s.tasks, output: v },
                  }))
                }
                placeholder="e.g., 1.50"
              />
            </div>
          </div>
          <div className="bg-body border-line space-y-4 rounded-lg border p-4">
            <h3 className="text-p-text font-semibold">Workflow Usage Costs</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <CostInput
                label="Input Cost ($ / 1M tokens)"
                value={settings.workflows.input}
                onChange={(v) =>
                  setSettings((s) => ({
                    ...s,
                    workflows: { ...s.workflows, input: v },
                  }))
                }
                placeholder="e.g., 1.00"
              />
              <CostInput
                label="Output Cost ($ / 1M tokens)"
                value={settings.workflows.output}
                onChange={(v) =>
                  setSettings((s) => ({
                    ...s,
                    workflows: { ...s.workflows, output: v },
                  }))
                }
                placeholder="e.g., 3.00"
              />
            </div>
          </div>
        </div>
        <div className="bg-2 flex justify-end rounded-b-2xl p-4">
          <button
            onClick={handleSave}
            className="flex items-center gap-2 rounded-lg bg-[#5652CF] px-4 py-2 font-semibold text-white transition-colors hover:bg-blue-600"
          >
            <IoSaveOutline /> Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};
